/**
 * RequestDetailSheet — one request-log row, opened (TRA-1412).
 *
 * A wide sheet from the right over the request log. It loads the saved
 * trace for the open row and hands it to `RequestDetailContent`; this file
 * owns only the chrome and the loading and error states.
 */

import { MONO, RequestDetailContent } from '@/components/models/home/RequestDetailContent'
import { formatTime, traceIdFromKey } from '@/components/models/home/requestLogRow'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { useTrace } from '@/hooks/useTraces'

function LoadingBody(): React.JSX.Element {
  return (
    <div className="space-y-3">
      <Skeleton className="h-6 w-1/2" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-40 w-full" />
    </div>
  )
}

interface RequestDetailSheetProps {
  /** The open row's trace key; null keeps the sheet closed. */
  traceKey: string | null
  /** The page this sheet is on, for the whole run's back arrow. */
  pageName: string
  onClose: () => void
}

export function RequestDetailSheet({ traceKey, pageName, onClose }: RequestDetailSheetProps): React.JSX.Element {
  const { data: doc, isLoading } = useTrace(traceKey)
  return (
    <Sheet open={traceKey !== null} onOpenChange={(open) => { if (!open) onClose() }}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-3xl">
        <SheetHeader className="mb-6">
          <SheetTitle>{doc === undefined ? 'Call details' : formatTime(doc.timestamp)}</SheetTitle>
          <SheetDescription>The gateway saved this request and its response.</SheetDescription>
        </SheetHeader>
        {traceKey === null ? null : isLoading ? (
          <LoadingBody />
        ) : doc === undefined ? (
          <div className="space-y-2">
            <p className="text-sm text-destructive">
              Couldn&apos;t load this call. It may no longer be saved.
            </p>
            <p className={MONO}>{traceIdFromKey(traceKey)}</p>
          </div>
        ) : (
          <RequestDetailContent doc={doc} traceKey={traceKey} pageName={pageName} />
        )}
      </SheetContent>
    </Sheet>
  )
}
